import React, { useCallback, useState, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { get, set, evaluateTemplate } from '@/components/formrenderer/utils/Utils';
import { useFormData } from '@/components/FormDataProvider';
import { useConfig } from '@/components/ConfigProvider';
import { useAuth } from '@/components/AuthProvider';
import { useValidation } from '@/components/ValidationProvider';
import navigateActionEditor from '@/layouts/actions/NavigateActionEditor.json';
import navigateUrlActionEditor from '@/layouts/actions/NavigateUrlActionEditor.json';
import pagenavActionEditor from '@/layouts/actions/PageNavActionEditor.json';
import loadformActionEditor from '@/layouts/actions/LoadFormActionEditor.json';
import messageboxActionEditor from '@/layouts/actions/MessageBoxActionEditor.json';
import postdataActionEditor from '@/layouts/actions/PostdataActionEditor.json';
import fetchFormDataActionEditor from '@/layouts/actions/FetchFormDataActionEditor.json';
import clearFormDataActionEditor from '@/layouts/actions/ClearFormDataActionEditor.json';
import setFormDataActionEditor from '@/layouts/actions/SetFormDataActionEditor.json';
import setFieldValueActionEditor from '@/layouts/actions/SetFieldValueActionEditor.json';
import uploadActionEditor from '@/layouts/actions/UploadActionEditor.json';
import moveRecordActionEditor from '@/layouts/actions/MoveRecordActionEditor.json';
import setCsvListActionEditor from '@/layouts/actions/SetCsvListActionEditor.json';
import actionResultActionEditor from '@/layouts/actions/ActionResultActionEditor.json';
import defaultActionEditor from '@/layouts/actions/DefaultActionEditor.json';
import { Action } from '@/components/formrenderer/ModalBox';
import { getDefaultControl } from '@/components/formrenderer/ControlEditor';

const evaluateActionValue = (value: any, data: any, root: any): any => {
  if (typeof value === 'string') {
    const exactTemplateMatch = value.match(/^\${([^|}]+)}$/);
    if (exactTemplateMatch) {
      const expression = exactTemplateMatch[1].trim();

      if (root && expression.startsWith('root.')) {
        return get(root, expression.substring(5).trim());
      }

      return get(data, expression);
    }

    return evaluateTemplate(value, data, root);
  }

  if (Array.isArray(value)) {
    return value.map(item => evaluateActionValue(item, data, root));
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [
        key,
        evaluateActionValue(item, data, root)
      ])
    );
  }

  return value;
};

export interface UseActionHandlerProps {
  onComplete?: () => void;
  onError?: () => void;
  onClose?: () => void;
}

/**
 * Hook that centralizes action handling for controls.
 * Returns executeAction(action, data) and UIAction state for rendering ModalBox.
 */
export default function useActionHandler({ onComplete, onError, onClose }: UseActionHandlerProps = {}) {
  const { apiUrl, docApiUrl } = useConfig();
  const { token } = useAuth();
  const router = useRouter();
  const { formData, setFormDataElement, fetchFormDataElement, fetchLookupData, setFormDataInfo, navigatePage, refreshForm } = useFormData();
  const { errors } = useValidation();

  const [UIAction, setUIAction] = useState<Action | null>(null);
  const executeActionRef = useRef<(action: Action, data?: any) => void>(() => { });

  const getEditorAction = useCallback((actiontype: string): Action => {
    let editor;
    switch (actiontype) {
      case 'navigate':
        editor = navigateActionEditor;
        break;
      case 'navigateurl':
        editor = navigateUrlActionEditor;
        break;
      case 'pagenav':
        editor = pagenavActionEditor;
        break;
      case 'loadform':
        editor = loadformActionEditor;
        break;
      case 'messagebox':
        editor = messageboxActionEditor;
        break;
      case 'postdata':
      case 'postdata2':
        editor = postdataActionEditor;
        break;
      case 'upload':
        editor = uploadActionEditor;
        break;
      case 'fetchformdata':
        editor = fetchFormDataActionEditor;
        break;
      case 'fetchlookupdata':
        editor = fetchFormDataActionEditor;
        break;
      case 'clearformdata':
        editor = clearFormDataActionEditor;
        break;
      case 'setformdata':
        editor = setFormDataActionEditor;
        break;
      case 'setfieldvalue':
        editor = setFieldValueActionEditor;
        break;
      case 'setcsvlist':
        editor = setCsvListActionEditor;
        break;
      case 'moverecord':
        editor = moveRecordActionEditor;
        break;
      case 'actionresult':
        editor = actionResultActionEditor;
        break;
      default:
        editor = defaultActionEditor;
        break;
    }
    return {
      type: 'messagebox',
      payload: {
        title: `Edit Action properties`,
        layout: editor,
        okbutton: true
      }
    };
  }, []);

  const setEditorAction = useCallback(async (action: Action) => {
    if (!action || !action.type) {
      console.warn('no action type specified');
      return;
    }
    setUIAction(getEditorAction(action.type));
  }, [getEditorAction]);

  const handleSuccess = useCallback((action: Action, data: any) => {
    if (action?.payload?.localOnly) {
      setUIAction(null);
      return;
    }

    if (action?.successAction?.type) {
      executeActionRef.current(action.successAction, data); // will be defined below
      return;
    }
    setUIAction(null);
    onComplete?.();
  }, [onComplete]);

  const handleFailure = useCallback((action: Action, data: any) => {
    if (action?.payload?.localOnly) {
      setUIAction(null);
      return;
    }

    if (action?.failureAction?.type) {
      executeActionRef.current(action.failureAction, data);
      return;
    }
    setUIAction(null);
    onError?.();
  }, [onError]);

  const handleClose = useCallback((action: Action, data: any) => {
    if (action?.payload?.localOnly) {
      setUIAction(null);
      return;
    }

    if (action?.completeAction?.type) {
      executeActionRef.current(action.completeAction, data);
      return;
    }
    setUIAction(null);
    onClose?.();
  }, [onClose]);

  const setActionResultValue = useCallback((payload: any, dataKey: string, value: any) => {
    if (payload?.noRefresh) {
      set(formData, dataKey, value);
      return;
    }

    setFormDataElement(dataKey, value);
  }, [formData, setFormDataElement]);

  const shouldStoreResultProperty = (payload: any, prop: string) => {
    if (prop === 'resultMessage' && payload?.suppressResultMessage === true) {
      return false;
    }

    if (prop === 'errors' && payload?.suppressResultErrors === true) {
      return false;
    }

    return true;
  };

  const getMessagesText = (messages: any) => {
    if (!messages || typeof messages !== 'object') {
      return '';
    }

    return Object.values(messages)
      .filter((message) => message != null && message !== '')
      .join('\n');
  };

  const applyActionResult = useCallback((action: Action, data: any, ret: any) => {
    const payload = action.payload ?? {};

    if (payload.resultDataKey) {
      for (const prop in ret) {
        if (shouldStoreResultProperty(payload, prop)) {
          setActionResultValue(payload, `${payload.resultDataKey}_${prop}`, ret[prop]);
        }
      }

      if (payload.storeResultErrorsText === true) {
        setActionResultValue(payload, `${payload.resultDataKey}_errorsText`, getMessagesText(ret?.errors));
        setActionResultValue(payload, `${payload.resultDataKey}_warningsText`, getMessagesText(ret?.warnings));
      }

      return !(payload.resultDataKey === "validationResult" && ret?.severity === 3);
    }

    if (payload.dataKey) {
      for (const prop in ret) {
        if (payload.setRoot) {
          set(formData, prop, ret[prop]);
        } else {
          set(data, prop, ret[prop]);
        }
      }
    }

    return true;
  }, [formData, setActionResultValue]);

  const executeAction = useCallback(async (action: Action, data: any = {}) => {
    if (!action || !action.type) {
      console.warn('no action specified');
      return;
    }
    // console.log(`executing action ${action.type}`);
    switch (action.type) {
      case 'clearformdata':
        if (action.payload?.dataKey) {
          setFormDataElement(action.payload.dataKey, null);
        }
        handleSuccess(action, data);
        break;

      case 'setformdata':
        if (action.payload?.dataKey && action.payload?.value !== undefined) {
          const value = evaluateActionValue(action.payload.value, data, formData);
          setFormDataElement(action.payload.dataKey, value);
        }
        handleSuccess(action, data);
        break;

      case 'setfieldvalue':
        if (action.payload?.dataKey && action.payload?.value !== undefined) {
          const dataContext = action.payload.dataContextKey ? get(formData, action.payload.dataContextKey, {}) : data;
          const value = evaluateActionValue(action.payload.value, data, formData);
          set(dataContext, action.payload.dataKey, value);
        }
        handleSuccess(action, data);
        break;

      case 'setcsvlist':
        if (action.payload?.srcDataKey && action.payload?.dstDataKey && action.payload?.separator !== undefined
          && action.payload.srcDataContextKey && action.payload.dstDataContextKey) {
          const srcDataContext = get(formData, action.payload.srcDataContextKey, {});
          const dstDataContext = get(formData, action.payload.dstDataContextKey, {});
          const csv = srcDataContext.map((i: any) => get(i, action.payload.srcDataKey, ''))
            .filter((v: string | null) => v != null && v !== '').join(action.payload?.separator);
          set(dstDataContext, action.payload.dstDataKey, csv);
        }
        handleSuccess(action, data);
        break;

      case 'moverecord':
        if (action.payload.srcDataContextKey && action.payload.dstDataContextKey) {
          const srcDataContext = get(formData, action.payload.srcDataContextKey, {});
          const dstDataContext = get(formData, action.payload.dstDataContextKey, {});
          const idx = srcDataContext.indexOf(data);
          if (idx > -1) {
            setFormDataElement(action.payload.srcDataContextKey, [...srcDataContext.slice(0, idx), ...srcDataContext.slice(idx + 1)]);
            setFormDataElement(action.payload.dstDataContextKey, [...dstDataContext, data]);
          }
        }
        handleSuccess(action, data);
        break;

      case 'fetchformdata':
        if (action.payload?.dataKey && action.payload.postDataKey !== undefined) {
          fetchFormDataElement(action.payload.dataKey, action.payload.postDataKey, action.payload.resultDataKey);
        }
        handleSuccess(action, data);
        break;

      case 'fetchlookupdata':
        if (action.payload?.dataKey) {
          fetchLookupData(action.payload.dataKey, action.payload.postDataKey, action.payload.resultDataKey);
        }
        handleSuccess(action, data);
        break;

      case 'loadform':
        if (action.payload?.dataKey && action.payload?.formMode) {
          const formKey = get(data, action.payload.dataKey, null);
          if (formKey) {
            setFormDataInfo(formKey, action.payload.formMode);
            router.push(`/edit?formKey=${formKey}&formMode=${action.payload.formMode}`);
            handleSuccess(action, data);
          } else {
            console.warn('formKey not found');
            handleFailure(action, data);
          }
        } else {
          handleFailure(action, data);
        }
        break;

      case 'refreshformdata':
        refreshForm();
        handleSuccess(action, data);
        break;

      case 'reloadpage':
        window.location.reload();
        handleSuccess(action, data);
        break;

      case 'actionresult': {
        setUIAction(null);
        const result = action.payload?.result ?? 'complete';
        if (result === 'error' || result === 'cancel') {
          onError?.();
        } else if (result === 'close') {
          onClose?.();
        } else {
          onComplete?.();
        }
        break;
      }

      case 'postdata':
      case 'postdata2': {
        if (action.payload?.endpoint) {
          const postData = action.payload.postDataKey ? get(data, action.payload.postDataKey, {}) : data;
          let endpoint = evaluateTemplate(action.payload.endpoint, data, formData);
          let url = `${apiUrl}/${endpoint}`;
          if (action.payload.dataKey) {
            const dataKeyValue = get(data, action.payload.dataKey, '');
            if (dataKeyValue) url += `/${dataKeyValue}`;
          }

          try {
            set(formData, "lastApiError", "");
            const response = await fetch(url, {
              method: 'POST',
              mode: 'cors',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify(postData)
            });

            if (!response.ok) {
              const errorText = await response.text();
              if (errorText) {
                // preserve the last API error in formData for error reporting
                set(formData, "lastApiError", errorText);
                handleFailure(action, data);
                if (action.payload.hideErrorMessage !== true) {
                  setFormDataElement("validationResult_resultMessage", errorText);
                  setFormDataElement("validationResult_severity", "error");
                }
                console.error(`Failed post data: ${response.status} ${errorText}`);
              }
              else {
                console.error(`Failed post data: ${response.status}`);
                handleFailure(action, data);
              }
              return;
            }

            if (action.type === 'postdata2') {
              if (action.payload.resultDataKey) {
                const ret = await response.json();
                const isSuccess = applyActionResult(action, data, ret);
                isSuccess ? handleSuccess(action, data) : handleFailure(action, data);
              } else if (action.payload.dataKey) {
                const ret = await response.json();
                applyActionResult(action, data, ret);
                handleSuccess(action, data);
              } else {
                const errorText = await response.text();
                if (errorText) {
                  // preserve the last API error in formData for error reporting
                  set(formData, "lastApiError", errorText);
                  handleFailure(action, data);
                  if (action.payload.hideErrorMessage !== true) {
                    setFormDataElement("validationResult_resultMessage", errorText);
                    setFormDataElement("validationResult_severity", 3);
                  }
                }
                else {
                  handleFailure(action, data);
                }
              }
            } else {
              handleSuccess(action, data);
            }
          } catch (err) {
            console.error('Error posting data:', err);
            handleFailure(action, data);
          }
        }
        break;
      }

      case 'navigate': {
        if (action.payload?.endpoint) {
          let endpoint = evaluateTemplate(action.payload.endpoint, data, formData);
          let url = `${apiUrl}/${endpoint}`;
          if (String(action.payload.isDocumentAPI).toLowerCase() === "true") {
            url = `${docApiUrl}/${endpoint}`;
            let filename = "downloaded.file";
            const postData = { formKey: data.FormKey || data.Pkey };
            try {
              const response = await fetch(url, {
                method: "POST",
                mode: "cors",
                headers: {
                  "Content-Type": "application/json",
                  "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify(postData),
              });
              if (!response.ok) throw new Error(`Failed to fetch document: ${response.status}`);
              const contentDisposition = response.headers.get("content-disposition");
              if (contentDisposition && contentDisposition.includes("filename=")) {
                const match = contentDisposition.match(/filename="?([^";]+)"?;/);
                if (match && match[1]) filename = match[1];
              }
              const blob = await response.blob();
              const blobUrl = window.URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = blobUrl;
              a.download = filename;
              document.body.appendChild(a);
              a.click();
              a.remove();
              window.URL.revokeObjectURL(blobUrl);
              handleSuccess(action, data);
            } catch (err) {
              console.error("Error fetching document:", err);
              handleFailure(action, data);
            }
          } else {
            if (action.payload.dataKey) {
              const dataKeyValue = get(data, action.payload.dataKey, '');
              if (dataKeyValue) url += `/${dataKeyValue}`;
            }
            window.open(url, action.payload.target ? action.payload.target : "_blank");
            handleSuccess(action, data);
          }
        } else {
          console.warn('Invalid navigate action payload.');
          handleFailure(action, data);
        }
        break;
      }

      case 'navigateurl': {
        if (action.payload?.url) {
          let url = evaluateTemplate(action.payload.url, data, formData);
          window.open(url, action.payload.target ? action.payload.target : "_blank");
          handleSuccess(action, data);
        } else {
          console.warn('Invalid navigateurl action payload.');
          handleFailure(action, data);
        }
        break;
      }

      case 'pagenav':
        if (action.payload?.page) {
          navigatePage(evaluateTemplate(action.payload.page, data, formData));
          handleSuccess(action, data);
        } else handleFailure(action, data);
        break;

      case 'messagebox':
        setUIAction(action);
        break;

      case 'editaction':
        setEditorAction(data);
        break;

      case 'editboxlayout':
        if (data && !data.layout) {
          console.log('Creating an empty messagebox layout.');
          data.layout = getDefaultControl("grid");
        }
        setUIAction({
          type: 'messagebox',
          payload: {
            title: `Edit MessageBox Layout`,
            layout: data.layout,
            mode: "design",
            okbutton: true
          }
        });
        break;

      default:
        console.warn(`Invalid action ${action.type}.`);
        break;
    }
  }, [
    apiUrl, docApiUrl, token, formData,
    setFormDataElement, fetchFormDataElement, fetchLookupData, setFormDataInfo, navigatePage,
    handleSuccess, handleFailure, setEditorAction, router, refreshForm, onComplete, onError, onClose,
    applyActionResult
  ]);

  executeActionRef.current = executeAction;

  return {
    executeAction,
    UIAction,
    setUIAction,
    handleSuccess,
    handleFailure,
    handleClose,
    applyActionResult
  };
}
